import { useMemo } from 'react';
import { ProgressBar } from 'components/atoms/index';
import { message } from 'antd';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';

interface Unit {
    id: number;
    code?: string;
    title: string;
    unitType?: string;
    unitCategory?: string;
    orderIndex?: number;
    status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
    progress: number;
    score?: number;
    lastAccessedDate?: string;
    textbookTitle?: string;
    textbookId?: number;
}

interface LearningDetailCardProps {
    status: 'completed' | 'waiting' | 'in-progress';
    progress: number;
    type?: 'learning' | 'homework';
    units?: Unit[];
    scheduleId?: number;
    disableNavigation?: boolean;
    dailyLimit?: {
        unlimited?: boolean;
        maxUnitsPerDay?: number;
        dayIndex?: number;
        allowedTotal?: number;
        completedBeforeToday?: number;
        completedToday?: number;
        todayAvailableAtStart?: number;
        remainingToday?: number;
    };
}

export default function LearningDetailCard({ status, progress, type = 'learning', units = [], scheduleId, disableNavigation = false, dailyLimit }: LearningDetailCardProps) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const slugPrefix = getSlugPrefix(pathname);
    
    // progress가 100%이면 status를 completed로 override
    const actualStatus = progress >= 100 ? 'completed' : status;

    // units에도 동일한 로직 적용
    const processedUnits = units.map(unit => ({
        ...unit,
        status: (unit.progress >= 100 ? 'COMPLETED' : unit.status) as 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED'
    }));

    const groupedUnits = useMemo(() => {
        const map = new Map<string, Unit[]>();
        processedUnits.forEach(unit => {
            const key = unit.textbookTitle || '교재 미지정';
            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key)!.push(unit);
        });
        return Array.from(map.entries()).map(([name, items]) => ({
            name,
            units: items,
        }));
    }, [processedUnits]);

    const getUnitStatusText = (unitStatus: string) => {
        switch (unitStatus) {
            case 'NOT_STARTED': return type === 'learning' ? '학습대기' : '숙제대기';
            case 'IN_PROGRESS': return type === 'learning' ? '학습중' : '숙제중';
            case 'COMPLETED': return type === 'learning' ? '학습완료' : '숙제완료';
            default: return type === 'learning' ? '학습대기' : '숙제대기';
        }
    };

    const getUnitStatusColor = (unitStatus: string) => {
        switch (unitStatus) {
            case 'NOT_STARTED': return 'text-gray-500';
            case 'IN_PROGRESS': return 'text-orange-500';
            case 'COMPLETED': return 'text-green-500';
            default: return 'text-gray-500';
        }
    };

    const handleUnitClick = (unitId: number) => {
        if (disableNavigation) {
            return;
        }
        if (!scheduleId) {
            console.error('scheduleId가 없습니다.');
            return;
        }

        // 일일 제한 체크: 오늘 시작 가능한 수를 모두 사용했고 새로운 유닛(NOT_STARTED)을 시작하려는 경우 차단
        const remainingToday = Number(dailyLimit?.remainingToday ?? NaN);
        if (!dailyLimit?.unlimited && !Number.isNaN(remainingToday)) {
            const target = processedUnits.find(u => u.id === unitId);
            if (target && target.status === 'NOT_STARTED' && remainingToday <= 0) {
                message.info('오늘 학습 가능한 유닛을 모두 완료했습니다. 내일 다시 시도해주세요.');
                return;
            }
        }

        // 유닛 학습/숙제 페이지로 이동
        const path = type === 'homework' ? '/homework/doing' : '/learning/doing';
        navigate(`${slugPrefix}${path}?scheduleId=${scheduleId}&unitId=${unitId}`);
    };

    return (
        <div className='overflow-hidden !rounded-lg card border border-gray-100 lg:border-none cursor-pointer' >
            <section className='flex justify-between items-center px-5 py-3 back_gra_ravendar'>
                <p className='text-lg font-bold'>{type === 'learning' ? '학습 진행 상태' : '숙제 진행 상태'}</p>
                <div className="flex gap-3 items-center">
                    {/* 일일 제한 안내 (옵션) */}
                    {dailyLimit && !dailyLimit.unlimited && (
                        <span className="text-xs text-gray-600">
                            오늘 풀 수 있는 유닛 {Math.max(0, Number(dailyLimit.remainingToday ?? 0))}개
                        </span>
                    )}
                    {/* 학습 상태 표시 */}
                    <span className={clsx(
                        "px-3 py-1 text-xs font-semibold rounded-full",
                        actualStatus === 'completed' && "text-green-600 bg-green-100",
                        actualStatus === 'waiting' && "text-blue-600 bg-blue-100",
                        
                    )}>
                        {actualStatus === 'completed' && (type === 'learning' ? '학습완료' : '숙제완료')}
                        {actualStatus === 'waiting' && (type === 'learning' ? '학습대기' : '숙제대기')}
                    </span>
                    
                    {/* 진행도 프로그레스바 - 학습중일 때만 표시 */}
                    {actualStatus === 'in-progress' && (
                        <div className="flex gap-1 items-center min-w-[100px]">
                            <ProgressBar progress={progress} size="small" showLabel headerClassName='!justify-end !w-auto !order-2' containerClassName='!flex-row !items-center !gap-2'/>
                        </div>
                    )}
                </div>
            </section>
                <div className='flex flex-col gap-2'>
                    <p className='px-5 pt-4 text-sm text-gray-500'>
                        {type === 'learning' ? 'Unit 들을 하나씩 학습해보세요!' : 'Unit 들을 하나씩 풀어보세요!'}
                    </p>
                    <ul className='flex flex-col gap-2 p-3'>
                        {processedUnits.length === 0 ? (
                            <li className='flex justify-center items-center p-4 text-gray-500 bg-gray-50 rounded-lg'>
                                배정된 유닛이 없습니다.
                            </li>
                        ) : (
                            groupedUnits.map((group, groupIndex) => (
                                <li key={`${group.name}-${groupIndex}`} className='flex flex-col gap-2 p-4 bg-gray-50 rounded-lg'>
                                    <p className='font-semibold text-gray-700'>{group.name}</p>
                                    <ul className='flex flex-col gap-2'>
                                        {group.units.map((unit, index) => (
                                            <li
                                                key={unit.id}
                                                className='flex justify-between items-center px-4 py-3 bg-white rounded-lg transition-colors cursor-pointer hover:bg-gray-100'
                                                onClick={() => handleUnitClick(unit.id)}
                                            >
                                                <div className='flex flex-col'>
                                                    <p className='font-medium'>{index + 1}. {unit.title}</p>
                                                    {unit.code && (
                                                        <span className='mt-1 text-xs text-gray-400'>({unit.code})</span>
                                                    )}
                                                </div>
                                                <p className={clsx('text-sm font-medium', getUnitStatusColor(unit.status))}>
                                                    {getUnitStatusText(unit.status)}
                                                    {unit.status === 'IN_PROGRESS' && ` (${unit.progress}%)`}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
        </div>
    )
}